import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { verifyInvitationThunk } from '../app/store/slices/groupSlice';
import { toast } from 'react-toastify';
import { fetchUserData } from '../app/store/slices/userSlice';

const AcceptInvitation = () => {
  const [searchParams] = useSearchParams();
  const [isVerifying, setIsVerifying] = useState(true);
  const token = searchParams.get('token');
  const navigate = useNavigate();
  const dispatch = useDispatch();

  useEffect(() => {
    handleVerification();

  }, [token]);

  const handleVerification = async () => {
    if (!token) {
      toast.error('Invalid invitation link');
      navigate('/signup');
      setIsVerifying(false);
      return;
    }

    // Check for auth data
    const authData = JSON.parse(localStorage.getItem('authData') || '{}');
    if (!authData?.accessToken) {
      localStorage.setItem('pendingInvitation', JSON.stringify({ token }));
      toast.info('Please login to accept the invitation');
      navigate('/login');
      setIsVerifying(false);
      return;
    }

    try {
      // Only fetch user data if authenticated
      const userData = await dispatch(fetchUserData()).unwrap();
      if (userData?.status === 404) {
        toast.error('User not found. Please Register First.');
        navigate('/signup');
        setIsVerifying(false);
        return;
      }

      // First verify the invitation token
      const result = await dispatch(verifyInvitationThunk(token)).unwrap();
      if (!result?.data?.email) {
        throw new Error('Invalid invitation');
      }

      // Check auth status after verifying token
      const freshAuthData = JSON.parse(localStorage.getItem('authData') || '{}');
      if (!freshAuthData?.user?.email) {
        localStorage.setItem('pendingInvitation', JSON.stringify({
          token,
          email: result.data.email
        }));
        toast.info('Please login to accept the invitation', {
          toastId: 'login-required'
        });
        navigate('/login');
        setIsVerifying(false);
        return;
      }

      // Auth exists but emails don't match
      if (result.data.email !== freshAuthData.user.email) {
        localStorage.removeItem('authData');
        localStorage.setItem('pendingInvitation', JSON.stringify({
          token,
          email: result.data.email
        }));
        toast.info(`Please login with ${result.data.email}`, {
          toastId: 'email-mismatch'
        });
        navigate('/login');
        setIsVerifying(false);
        return;
      }

      // Everything matches - store token and go to dashboard
      localStorage.setItem('pendingInvitation', JSON.stringify({
        token,
        email: result.data.email
      }));
      navigate('/dashboard');

    } catch (error) {
      console.error('Verification error:', error);
      toast.error(error.message || 'Invalid invitation link', {
        toastId: 'verification-error'
      });
      navigate('/login');
    } finally {
      setIsVerifying(false);
    }
  };

  // Show loading state while verifying
  if (isVerifying) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-600"></div>
          <p className="mt-4 text-gray-600">Verifying invitation...</p>
        </div>
      </div>
    );
  }

  return null;
};

export default AcceptInvitation;